
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreateFAQFormProps {
  organizationId: string;
  sectionId: string;
  onFAQCreated: () => void;
}

export const CreateFAQForm = ({ organizationId, sectionId, onFAQCreated }: CreateFAQFormProps) => {
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!question.trim() || !answer.trim()) {
      toast.error("Please fill in both question and answer");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get the highest position number for ordering within this section
      const { data: maxPosition } = await supabase
        .from('faq_items')
        .select('position')
        .eq('organization_id', organizationId)
        .eq('section_id', sectionId)
        .order('position', { ascending: false })
        .limit(1);

      const nextPosition = maxPosition && maxPosition.length > 0 ? maxPosition[0].position + 1 : 1;

      // Create the FAQ item (no longer sync to Frank on create)
      const { data, error } = await supabase
        .from('faq_items')
        .insert({
          question: question.trim(),
          answer: answer.trim(),
          organization_id: organizationId,
          section_id: sectionId,
          position: nextPosition,
          created_by: (await supabase.auth.getUser()).data.user?.id
        })
        .select()
        .single();

      if (error) throw error;

      console.log('FAQ created successfully:', data);
      toast.success("FAQ item created successfully");

      // Reset form
      setQuestion("");
      setAnswer("");
      setShowForm(false);
      onFAQCreated();
      
    } catch (error: any) {
      console.error('Error creating FAQ:', error);
      toast.error('Failed to create FAQ item');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!showForm) {
    return (
      <Button
        onClick={() => setShowForm(true)}
        className="w-full sm:w-auto min-h-[44px] text-base mb-4"
      >
        <Plus className="h-4 w-4 mr-2" />
        Add FAQ Item
      </Button>
    );
  }

  return (
    <Card className="mb-4 shadow-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg sm:text-xl">Add New FAQ Item</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="question" className="block text-sm font-medium mb-2">
              Question
            </label>
            <Input
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Enter the question..."
              disabled={isSubmitting}
              required
              className="text-base min-h-[44px]"
            />
          </div>
          
          <div>
            <label htmlFor="answer" className="block text-sm font-medium mb-2">
              Answer
            </label>
            <Textarea
              id="answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Enter the answer..."
              disabled={isSubmitting}
              required
              rows={6}
              className="text-base leading-relaxed min-h-[120px] resize-none"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 min-h-[44px] text-base"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create FAQ
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowForm(false);
                setQuestion("");
                setAnswer("");
              }}
              disabled={isSubmitting}
              className="min-h-[44px] text-base"
            >
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
